import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ArrowUpRight, Phone, ShieldCheck } from "lucide-react";
import { PageHero } from "../components/site/PageHero";
import { Reveal } from "../components/site/Reveal";
import { PRICING, BUSINESS } from "../data/content";

const SectionHead = ({ eyebrow, title, dark }) => (
  <div className="mb-10">
    <Reveal>
      <span className={`font-mono text-[12px] font-bold uppercase tracking-[0.25em] ${dark ? "text-fc-orange" : "text-accent"}`}>{eyebrow}</span>
    </Reveal>
    <Reveal delay={0.05}>
      <h2 className={`mt-4 font-display font-bold tracking-tight leading-[0.98] text-3xl md:text-5xl ${dark ? "text-white" : "text-fc-ink"}`}>{title}</h2>
    </Reveal>
  </div>
);

const PriceRow = ({ service, price }) => (
  <div className="flex items-center justify-between gap-4 py-4 border-b border-fc-ink/10">
    <span className="font-semibold text-fc-ink">{service}</span>
    <span className="font-display font-bold text-accent whitespace-nowrap">{price}</span>
  </div>
);

const InfoCard = ({ service, price, desc }) => (
  <div className="bg-white border border-fc-ink/10 p-7 h-full">
    <div className="font-display text-xl font-semibold text-fc-ink">{service}</div>
    <div className="mt-2 font-display text-2xl font-bold text-accent">{price}</div>
    {desc && <p className="mt-3 text-sm text-fc-ink2">{desc}</p>}
  </div>
);

export default function PricingPage() {
  const navigate = useNavigate();

  return (
    <>
      <PageHero
        eyebrow="HVAC Service & Pricing"
        title="Clear starting prices."
        subtitle="Clear Starting Prices. Professional Service. No Guesswork."
      />

      {/* Residential Services */}
      <section data-testid="pricing-residential" className="surface-light py-20 lg:py-28">
        <div className="mx-auto w-[92%] max-w-[1000px]">
          <SectionHead eyebrow="Residential Services" title="Residential HVAC Services" />
          <Reveal>
            <div className="bg-white border border-fc-ink/10 p-6 sm:p-8">
              {PRICING.residential.map((r) => <PriceRow key={r.service} {...r} />)}
            </div>
          </Reveal>
        </div>
      </section>

      {/* AC Installation */}
      <section data-testid="pricing-installation" className="surface-dark text-white py-20 lg:py-28">
        <div className="mx-auto w-[92%] max-w-[1000px]">
          <SectionHead eyebrow="AC Installation" title="New System Installation" dark />
          <Reveal>
            <div className="border border-white/12 bg-white/[0.04] p-8 lg:p-10 flex flex-col lg:flex-row lg:items-center gap-8">
              <div className="flex-1">
                <div className="font-display text-2xl font-semibold">{PRICING.installation.title}</div>
                <div className="mt-2 font-display text-4xl font-bold text-fc-orange">{PRICING.installation.price}</div>
                <p className="mt-4 text-white/65 max-w-xl">{PRICING.installation.desc}</p>
              </div>
              <button
                data-testid="installation-cta"
                onClick={() => navigate("/contact")}
                className="group inline-flex items-center gap-2 bg-fc-orange text-white px-7 py-4 font-bold uppercase tracking-wide text-sm hover:brightness-95 transition-all whitespace-nowrap"
              >
                {PRICING.installation.cta}
                <ArrowUpRight size={18} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
              </button>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Maintenance Plans */}
      <section data-testid="pricing-plans" className="surface-light py-20 lg:py-28">
        <div className="mx-auto w-[92%] max-w-[1280px]">
          <SectionHead eyebrow="Maintenance Plans" title="F&C Comfort Care" />
          <div className="grid md:grid-cols-3 gap-6">
            {PRICING.plans.map((p, i) => (
              <motion.div
                key={p.name}
                data-testid={`plan-${p.name.toLowerCase()}`}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.6, delay: i * 0.1 }}
                className={`relative flex flex-col p-8 border ${p.featured ? "bg-fc-navy text-white border-fc-navy shadow-[0_30px_70px_rgba(11,44,92,0.25)]" : "bg-white text-fc-ink border-fc-ink/10"}`}
              >
                {p.featured && (
                  <span className="absolute -top-3 left-8 bg-fc-orange text-white text-[10px] font-bold uppercase tracking-widest px-3 py-1">Most Popular</span>
                )}
                <div className={`font-mono text-[11px] font-bold uppercase tracking-[0.25em] ${p.featured ? "text-fc-orange" : "text-accent"}`}>{p.name}</div>
                <div className="mt-4 flex items-end gap-1">
                  <span className="font-display text-5xl font-bold tracking-tight">{p.price}</span>
                  <span className={`mb-1.5 text-sm ${p.featured ? "text-white/60" : "text-fc-ink2"}`}>{p.period}</span>
                </div>
                <ul className="mt-6 space-y-3 flex-1">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2.5 text-sm">
                      <Check size={16} className={`mt-0.5 shrink-0 ${p.featured ? "text-fc-orange" : "text-fc-sky"}`} />
                      <span className={p.featured ? "text-white/85" : "text-fc-ink2"}>{f}</span>
                    </li>
                  ))}
                </ul>
                <button
                  data-testid={`plan-cta-${p.name.toLowerCase()}`}
                  onClick={() => navigate("/contact")}
                  className={`mt-8 inline-flex items-center justify-center gap-2 px-6 py-3.5 font-bold uppercase tracking-wide text-sm transition-all ${p.featured ? "bg-fc-orange text-white hover:brightness-95" : "bg-fc-navy text-white hover:bg-fc-orange"}`}
                >
                  Choose {p.name}
                </button>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Commercial */}
      <section data-testid="pricing-commercial" className="surface-light py-20 lg:py-28">
        <div className="mx-auto w-[92%] max-w-[1280px]">
          <SectionHead eyebrow="Commercial HVAC" title="Commercial HVAC" />
          <div className="grid md:grid-cols-3 gap-6">
            {PRICING.commercial.map((c, i) => (
              <Reveal key={c.service} delay={i * 0.08}><InfoCard {...c} /></Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Walk-In Coolers & Electrical */}
      <section data-testid="pricing-walkin-electrical" className="surface-light pb-20 lg:pb-28">
        <div className="mx-auto w-[92%] max-w-[1280px]">
          <SectionHead eyebrow="Walk-In Coolers & Electrical" title="Refrigeration & Electrical" />
          <div className="grid lg:grid-cols-2 gap-10">
            <div>
              <h3 className="font-display text-xl font-semibold text-fc-ink mb-4">Walk-In Cooler Service</h3>
              <div className="bg-white border border-fc-ink/10 p-6">
                {PRICING.walkin.map((r) => <PriceRow key={r.service} {...r} />)}
              </div>
            </div>
            <div>
              <h3 className="font-display text-xl font-semibold text-fc-ink mb-4">HVAC-Related Electrical</h3>
              <div className="bg-white border border-fc-ink/10 p-6">
                {PRICING.electrical.map((r) => <PriceRow key={r.service} {...r} />)}
              </div>
            </div>
          </div>

          {/* Pricing Notice */}
          <Reveal>
            <div data-testid="pricing-notice" className="mt-12 border-l-4 border-fc-orange bg-white p-6">
              <div className="font-mono text-[11px] font-bold uppercase tracking-widest text-accent mb-2">Pricing Notice</div>
              <p className="text-sm text-fc-ink2">{PRICING.notice}</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Tagline band */}
      <section className="bg-fc-orange text-white py-12">
        <div className="mx-auto w-[92%] max-w-[1280px] flex items-center justify-center gap-3 text-center">
          <ShieldCheck size={22} className="hidden sm:block" />
          <p className="font-display text-xl md:text-3xl font-bold tracking-tight">
            No Surprises. Upfront Pricing. Professional Service.
          </p>
        </div>
      </section>

      {/* Bottom CTA */}
      <section data-testid="pricing-bottom-cta" className="surface-dark text-white py-20 lg:py-28">
        <div className="mx-auto w-[92%] max-w-[1280px] text-center">
          <Reveal>
            <h2 className="font-display font-bold tracking-tight leading-[0.98] text-3xl md:text-5xl">Not sure what you need?</h2>
          </Reveal>
          <Reveal delay={0.05}>
            <p className="mt-4 text-white/65 max-w-xl mx-auto">Let our team evaluate your system and recommend the right solution.</p>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="mt-8 flex flex-wrap gap-4 justify-center">
              <a href={BUSINESS.phoneHref} data-testid="pricing-call-btn" className="inline-flex items-center gap-2 bg-fc-orange text-white px-7 py-4 font-bold uppercase tracking-wide text-sm hover:brightness-95 transition-all">
                <Phone size={16} /> Call {BUSINESS.phone}
              </a>
              <button onClick={() => navigate("/contact")} data-testid="pricing-request-btn" className="inline-flex items-center gap-2 border border-white/40 hover:border-white px-7 py-4 font-bold uppercase tracking-wide text-sm transition-colors">
                Request Service
              </button>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
